import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Publication } from '@/mocks/publications';

const typeColors: Record<string, string> = {
  'Book': 'bg-amber-100 text-amber-800',
  'Journal Article': 'bg-emerald-100 text-emerald-800',
  'Magazine Publication': 'bg-rose-100 text-rose-800',
  'Gazette': 'bg-violet-100 text-violet-800',
  'Essay': 'bg-sky-100 text-sky-800',
  'Catalogue': 'bg-orange-100 text-orange-800',
};

interface LightboxProps {
  images: string[];
  initialIndex: number;
  title: string;
  onClose: () => void;
}

function Lightbox({ images, initialIndex, title, onClose }: LightboxProps) {
  const [current, setCurrent] = useState(initialIndex);

  const prev = useCallback(() => setCurrent((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setCurrent((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90" onClick={onClose}>
      <button
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer"
        onClick={onClose}
      >
        <i className="ri-close-line text-xl" />
      </button>
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-sans">
          {current + 1} / {images.length}
        </div>
      )}
      {images.length > 1 && (
        <button
          className="absolute left-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
          onClick={(e) => { e.stopPropagation(); prev(); }}
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
      )}
      <div className="max-w-3xl max-h-[85vh] flex items-center justify-center px-16" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[current]}
          alt={`${title} image ${current + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded"
        />
      </div>
      {images.length > 1 && (
        <button
          className="absolute right-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
          onClick={(e) => { e.stopPropagation(); next(); }}
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>
      )}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
              className={`w-12 h-16 rounded overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${i === current ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-75'}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface Props {
  publication: Publication;
  relatedPublications: Publication[];
}

export default function PublicationDetail({ publication, relatedPublications }: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const allImages = [
    ...(publication.coverImage ? [publication.coverImage] : []),
    ...(publication.additionalImages ?? []),
  ];

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <div className="bg-[#f9f6f1] border-b border-stone-200 py-4">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center gap-2 text-sm font-sans text-gray-500">
            <Link to="/" className="hover:text-gray-900 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400" />
            <Link to="/publications" className="hover:text-gray-900 transition-colors cursor-pointer">Publications</Link>
            <i className="ri-arrow-right-s-line text-gray-400" />
            <span className="text-gray-900 line-clamp-1">{publication.title}</span>
          </div>
        </div>

        {/* Main content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left — images */}
            <div>
              {/* Primary cover */}
              {publication.coverImage && (
                <div
                  className="w-full bg-[#f9f6f1] rounded-lg overflow-hidden cursor-pointer group relative mb-4"
                  onClick={() => openLightbox(0)}
                >
                  <img
                    src={publication.coverImage}
                    alt={publication.title}
                    className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-sans text-stone-700">
                      <i className="ri-zoom-in-line text-sm" />
                      Enlarge
                    </div>
                  </div>
                </div>
              )}

              {/* Additional images thumbnails */}
              {publication.additionalImages && publication.additionalImages.length > 0 && (
                <div className="flex gap-3 flex-wrap">
                  {allImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => openLightbox(i)}
                      className="w-20 h-24 rounded overflow-hidden border-2 border-stone-200 hover:border-[#b89a6a] transition-colors cursor-pointer flex-shrink-0"
                    >
                      <img src={img} alt="" className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right — details */}
            <div>
              {/* Type badge */}
              <div className="flex items-center gap-3 mb-4">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeColors[publication.type] ?? 'bg-gray-100 text-gray-700'}`}>
                  {publication.type}
                </span>
                <span className="text-xs text-gray-400 font-sans">{publication.year}</span>
              </div>

              {/* Title */}
              <h1 className="font-serif text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {publication.title}
              </h1>



              {/* Abstract */}
              <div className="mb-8">
                <h2 className="font-sans text-xs uppercase tracking-widest text-gray-400 mb-3">About this publication</h2>
                <p className="font-sans text-base text-gray-700 leading-relaxed">{publication.abstract}</p>
              </div>

              {/* Meta details */}
              <div className="bg-[#f9f6f1] rounded-lg p-6 mb-8">
                <h2 className="font-sans text-xs uppercase tracking-widest text-gray-400 mb-4">Publication Details</h2>
                <dl className="space-y-3">
                  <div className="flex items-start gap-3">
                    <dt className="font-sans text-xs text-gray-500 w-24 shrink-0 pt-0.5">Author</dt>
                    <dd className="font-sans text-sm text-gray-900">{publication.author ?? 'Oludamola Adebowale'}</dd>
                  </div>
                  <div className="flex items-start gap-3">
                    <dt className="font-sans text-xs text-gray-500 w-24 shrink-0 pt-0.5">Publisher</dt>
                    <dd className="font-sans text-sm text-gray-900">{publication.publisher}</dd>
                  </div>
                  <div className="flex items-start gap-3">
                    <dt className="font-sans text-xs text-gray-500 w-24 shrink-0 pt-0.5">Year</dt>
                    <dd className="font-sans text-sm text-gray-900">{publication.year}</dd>
                  </div>
                  {publication.pages && (
                    <div className="flex items-start gap-3">
                      <dt className="font-sans text-xs text-gray-500 w-24 shrink-0 pt-0.5">Pages</dt>
                      <dd className="font-sans text-sm text-gray-900">{publication.pages} pp.</dd>
                    </div>
                  )}
                  {publication.isbn && (
                    <div className="flex items-start gap-3">
                      <dt className="font-sans text-xs text-gray-500 w-24 shrink-0 pt-0.5">ISBN</dt>
                      <dd className="font-sans text-sm text-gray-900 font-mono">{publication.isbn}</dd>
                    </div>
                  )}
                  {publication.doi && (
                    <div className="flex items-start gap-3">
                      <dt className="font-sans text-xs text-gray-500 w-24 shrink-0 pt-0.5">DOI</dt>
                      <dd className="font-sans text-sm text-gray-900 font-mono">{publication.doi}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Topics */}
              <div className="mb-8">
                <h2 className="font-sans text-xs uppercase tracking-widest text-gray-400 mb-3">Topics</h2>
                <div className="flex flex-wrap gap-2">
                  {publication.topic.map((t) => (
                    <span key={t} className="text-xs bg-stone-100 text-stone-600 px-3 py-1 rounded-full font-sans">
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/publications"
                  className="inline-flex items-center justify-center gap-2 font-sans text-sm font-medium border border-stone-300 text-gray-700 px-6 py-3 rounded-md hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line" />
                  </div>
                  All Publications
                </Link>
                {(publication.amazonUrl || publication.buyNowUrl) ? (
                  <a
                    href={publication.amazonUrl ?? publication.buyNowUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 font-sans text-sm font-medium bg-gray-900 text-white px-6 py-3 rounded-md hover:bg-[#b89a6a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-shopping-bag-line" />
                    </div>
                    Buy Now
                  </a>
                ) : (
                  <a
                    href="mailto:damola@oludamolaadebowale.com"
                    className="inline-flex items-center justify-center gap-2 font-sans text-sm font-medium bg-gray-900 text-white px-6 py-3 rounded-md hover:bg-[#b89a6a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line" />
                    </div>
                    Enquire About This Publication
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Related publications */}
          {relatedPublications.length > 0 && (
            <div className="mt-24 pt-16 border-t border-stone-100">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-8">Related Publications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPublications.map((pub) => (
                  <Link
                    key={pub.id}
                    to={`/publications/${pub.id}`}
                    className="bg-[#f9f6f1] rounded-lg overflow-hidden hover:bg-[#f3ede4] transition-colors duration-300 cursor-pointer flex flex-col group"
                  >
                    {pub.coverImage && (
                      <div className="w-full bg-stone-200 overflow-hidden flex-shrink-0">
                        <img
                          src={pub.coverImage}
                          alt={pub.title}
                          className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${typeColors[pub.type] ?? 'bg-gray-100 text-gray-700'}`}>
                          {pub.type}
                        </span>
                        <span className="text-xs text-gray-400 font-sans">{pub.year}</span>
                      </div>
                      <h3 className="font-serif text-base font-semibold text-gray-900 leading-snug line-clamp-2">
                        {pub.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {lightboxOpen && allImages.length > 0 && (
        <Lightbox
          images={allImages}
          initialIndex={lightboxIndex}
          title={publication.title}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
